import { useState } from "react";
import { X, ZoomIn } from "lucide-react";

/**
 * ImagePreview - shows a small preview of an image URL and opens a full-screen
 * lightbox on click with pan/zoom support (wheel to zoom, drag to pan).
 * Used across the admin panel so every image field has zoom capability.
 */
export const ImagePreview = ({ url, alt = "preview", className = "", size = "md" }) => {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  if (!url) return null;

  const sizeCls = { sm: "w-16 h-16", md: "w-24 h-24", lg: "w-32 h-32" }[size] || "w-24 h-24";

  const onWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.002;
    setScale((s) => Math.max(0.5, Math.min(6, s + delta)));
  };
  const onMouseDown = (e) => {
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const onMouseMove = (e) => {
    if (!dragging) return;
    setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const onMouseUp = () => setDragging(false);

  const reset = () => { setScale(1); setOffset({ x: 0, y: 0 }); };

  return (
    <>
      <button
        type="button"
        onClick={() => { reset(); setOpen(true); }}
        data-testid="image-preview-btn"
        className={`relative ${sizeCls} rounded-lg overflow-hidden border border-white/10 group ${className}`}
        title="Böyütmək üçün klik"
      >
        <img src={url} alt={alt} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
          <ZoomIn className="w-6 h-6 text-white" />
        </div>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
          onClick={() => setOpen(false)}
          data-testid="image-zoom-overlay"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 rounded-full p-2 z-10"
            data-testid="close-zoom"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <div className="absolute top-4 left-4 flex gap-2 z-10">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setScale((s) => Math.min(6, s + 0.5)); }}
              className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded text-sm font-mono"
            >+</button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setScale((s) => Math.max(0.5, s - 0.5)); }}
              className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded text-sm font-mono"
            >−</button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); reset(); }}
              className="bg-white/10 hover:bg-white/20 text-white px-3 py-2 rounded text-xs uppercase tracking-widest"
            >Reset</button>
            <span className="bg-white/5 text-white/70 px-3 py-2 rounded text-sm font-mono">
              {Math.round(scale * 100)}%
            </span>
          </div>
          <div
            className="w-full h-full flex items-center justify-center overflow-hidden select-none"
            onWheel={onWheel}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            style={{ cursor: dragging ? "grabbing" : "grab" }}
          >
            <img
              src={url}
              alt={alt}
              draggable={false}
              className="max-w-[90vw] max-h-[85vh] object-contain transition-transform"
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                transformOrigin: "center center",
                transitionDuration: dragging ? "0ms" : "120ms",
              }}
            />
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-xs font-mono uppercase tracking-widest">
            Scroll: zoom · Sürüklə: hərəkət
          </div>
        </div>
      )}
    </>
  );
};

export default ImagePreview;
